import { DistributorProfileFormData } from '@/types/profile/DistributorProfileFormData';
import { Control, useController } from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledDropZone from '../styled/StyledDropZone';
import { COUNTRIES } from '../../../../shared/CountriesData';
import StyledSelect from '../styled/StyledSelect';
import HorizontalLine from '../ui/HorizontalLine';
import { useGetMyBills } from '@/hooks/useBills';
import Loader from '../ui/Loader';
import StyledTable from '../styled/StyledTable';
import { BILL_COLUMN_DEF } from '@/types/bills/billColumnDef';
import { useMemo, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import { LOCALES, useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LabelValue } from '@/types/global';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useGetMe } from '@/api/user.api';
import { DistributorKind } from '@/types/auth/DistributorKind';

interface DistributorProfileGeneralTabFormProps {
  control: Control<DistributorProfileFormData>;
  isEditEnabled: boolean;
  isAdmin: boolean;
}

const DistributorProfileGeneralTabForm = ({
  control,
  isEditEnabled,
  isAdmin,
}: DistributorProfileGeneralTabFormProps) => {
  const { t } = useTranslation();
  const { data: userData } = useGetMe();
  const isTourOperator =
    userData?.agency?.kind === DistributorKind.TourOperator;
  const getTranslatedConst = useTranslatedConst();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: bills, isLoading, isSuccess } = useGetMyBills();

  const { changeLanguage } = useLanguage();
  const {
    field: { onChange: onChangeMainLanguage },
  } = useController({
    control,
    name: 'mainLanguage',
  });

  const LANGUAGES = useMemo(() => {
    return LOCALES.map((lang) => ({
      label: t(`common.languages.${lang}`),
      value: lang,
    }));
  }, [t]);

  return (
    <>
      {isAdmin && (
        <>
          <StyledFormSection sectionTitle="Informations administratives">
            <StyledInput
              control={control}
              title={
                isTourOperator
                  ? t('pages.profile.tourOperatorName')
                  : t('pages.profile.agencyName')
              }
              name="agency.name"
              placeholder={t('pages.profile.agencyNamePlaceholder')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <StyledDropZone
              control={control}
              name="agency.logo"
              title={t('pages.profile.logo')}
              placeholder={t('pages.profile.logoPlaceholder')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <StyledInput
              control={control}
              title={t('pages.profile.address')}
              name="agency.address"
              placeholder={t('pages.profile.addAddress')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <div className="flex flex-col md:flex-row gap-4">
              <StyledInput
                control={control}
                title={t('pages.profile.city')}
                name="agency.city"
                placeholder={t('pages.profile.addCity')}
                disabled={!isEditEnabled || !isAdmin}
              />
              <StyledInput
                control={control}
                title={t('pages.profile.postalCode')}
                name="agency.postalCode"
                type="number"
                hideNumberFieldArrows
                placeholder={t('pages.profile.addPostalCode')}
                disabled={!isEditEnabled || !isAdmin}
              />
            </div>
            <StyledSelect
              options={COUNTRIES}
              control={control}
              title={t('pages.profile.country')}
              name="agency.country"
              placeholder={t('pages.profile.selectCountry')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <StyledInput
              control={control}
              title={t('pages.profile.siren')}
              name="agency.siren"
              placeholder={t('pages.profile.addCode')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <StyledInput
              control={control}
              title={t('pages.profile.website')}
              name="agency.website"
              placeholder={t('pages.profile.websitePlaceholder')}
              disabled={!isEditEnabled || !isAdmin}
            />
            <MarginRateStyledInput
              control={control}
              title={t('pages.profile.marginRate')}
              name="agency.marginRate"
              placeholder={t('pages.profile.addRate')}
              disabled={!isEditEnabled || !isAdmin}
            />
          </StyledFormSection>
          <HorizontalLine />
        </>
      )}
      <StyledFormSection sectionTitle="Contact principal">
        <StyledSelect
          options={LANGUAGES}
          control={control}
          title={t('pages.profile.mainLanguage')}
          name="mainLanguage"
          onChange={(lang) => {
            const { value } = lang as LabelValue<string>;
            onChangeMainLanguage(lang);
            changeLanguage(value as SupportedLocale);
          }}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            title={t('pages.profile.lastName')}
            name="lastName"
            disabled={!isEditEnabled}
          />
          <StyledInput
            control={control}
            title={t('pages.profile.firstName')}
            name="firstName"
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            title={t('pages.profile.email')}
            name="email"
            type="email"
            disabled={!isEditEnabled || !isAdmin}
          />
          <StyledInput
            control={control}
            title={t('pages.profile.phone')}
            name="phoneNumber"
            type="tel"
            placeholder={t('pages.profile.addPhoneNumber')}
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-1/2">
            <StyledInput
              control={control}
              title={t('pages.profile.password')}
              name="password"
              type="password"
              disabled={!isEditEnabled}
            />
          </div>
        </div>
      </StyledFormSection>
      {isAdmin && !isTourOperator && (
        <>
          <HorizontalLine />
          <StyledFormSection sectionTitle={t('pages.profile.networkTitle')}>
            <StyledSelect
              options={[]}
              control={control}
              name="network"
              title={t('pages.profile.networkTitle')}
              placeholder={t('pages.profile.selectNetwork')}
              creatable
              isClearable
              disabled={!isEditEnabled || !isAdmin}
            />
          </StyledFormSection>
        </>
      )}
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess &&
        bills.length > 0 && (
          <>
            <HorizontalLine />
            <StyledFormSection
              sectionTitle={t('pages.profile.bills')}
              shouldTakeFullWidth
            >
              <div className="w-full">
                <StyledTable
                  columns={getTranslatedConst(BILL_COLUMN_DEF)}
                  data={bills}
                  getRowId={(row) => row.id}
                  onDownloadRow={(row) => {
                    window.open(row.original.fileUrl, '_blank');
                  }}
                  sorting={sorting}
                  setSorting={setSorting}
                  clientSideSorting
                />
              </div>
            </StyledFormSection>
          </>
        )
      )}
    </>
  );
};

export default DistributorProfileGeneralTabForm;
